/* eslint-disable @angular-eslint/no-output-on-prefix */
import { Component, OnInit, ElementRef, OnDestroy, input, output, signal, viewChild, inject } from '@angular/core';
import { FocusableOption } from '@angular/cdk/a11y';
import { NgClass } from '@angular/common';

import { AvailableIcons, ApIconComponent } from '@ap/icon';

import { ApAccordionService } from '../accordion.service';

let nextUniqueId = 0;

/**
 * Directive for accordion items within apAccordion
 * @deprecated in favor of [`@asn/accordion`](https://designsystem.acc.aws.devolksbank.net/index.html?path=/docs/web-components-accordion--docs). To be removed with the Angular 23 migration.
 */
@Component({
    selector: 'ap-accordion-item',
    templateUrl: './item.component.html',
    styleUrls: ['./item.component.scss'],
    imports: [NgClass, ApIconComponent],
    host: {
        '[class.ap-accordion-item__border--disabled]': 'borderDisabled()' // eslint-disable-line @typescript-eslint/naming-convention
    }
})
export class ApAccordionItemComponent implements OnInit, OnDestroy, FocusableOption {
    private readonly accordionService = inject(ApAccordionService);

    /**
     * Required - The title to show in the header of the accordion item
     */
    public readonly accordionItemTitle = input.required<string>();

    /**
     * The name of the icon to show in the accordion item
     */
    public readonly iconName = input<AvailableIcons>(undefined);

    /**
     * The sub title to show in the header of the accordion item
     */
    public readonly subTitle = input<string>(undefined);

    /**
     * This will initially open the accordion item
     */
    public readonly initOpen = input(false);

    /**
     * Id of accordion. If none is passed a random id will be generated
     */
    public readonly id = input<string>(this.getUuid());

    /**
     * Disable the item, show it like it is empty
     */
    public readonly isEmpty = input(false);

    /**
     * Disable the border of the item
     */
    public readonly borderDisabled = input(false);

    /**
     * If provided, this function will trigger when the item is closed
     */
    public readonly onClose = output<any>();

    /**
     * If provided, this function will trigger when the item is opened
     */
    public readonly onOpen = output<any>();

    /**
     * If provided, this function will trigger when the item is clicked
     */
    public readonly onClick = output<any>();

    protected readonly headingElementRef = viewChild<ElementRef>('heading');

    /**
     * If accordion item is open. Can be set from parent `ApAccordionComponent`
     * @internal
     */
    public readonly isOpen = signal(false);

    /**
     * Variant of accordion item. Set from parent `ApAccordionComponent`
     * @internal
     */
    public readonly variant = signal<string>(null);

    protected headingId = `ap-accordion-heading-${this.id()}`;
    protected contentId = `ap-accordion-content-${this.id()}`;

    public ngOnInit(): void {
        this.accordionService.register(this);
        this.isOpen.set(this.initOpen());
    }

    public ngOnDestroy(): void {
        this.accordionService.unregister(this);
    }

    protected getUuid() {
        ++nextUniqueId;
        return `${nextUniqueId}`;
    }

    protected click() {
        this.onClick.emit(undefined);
        this.toggle();
    }

    protected toggle() {
        this.isOpen.set(!this.isOpen());
        if (this.isOpen()) {
            this.onOpen.emit(undefined);
        } else {
            this.onClose.emit(undefined);
        }
    }

    /**
     * Function to focus the accordion item
     */
    public focus() {
        const headingElementRef = this.headingElementRef();
        if (headingElementRef.nativeElement && typeof headingElementRef.nativeElement.focus === 'function') {
            headingElementRef.nativeElement.focus();
        }
    }
}
